import { Canvas } from "@react-three/fiber";
import { OrbitControls, Float, MeshDistortMaterial } from "@react-three/drei";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-950 text-white min-h-[90vh] flex items-center justify-center">
      {/* Background Glow Effects */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-indigo-600/40 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-600/30 rounded-full blur-[140px]" />
      </div>

      {/* 3D Canvas */}
      <div className="absolute inset-0 z-0 opacity-60">
        <Canvas camera={{ position: [0, 0, 4] }}>
          <ambientLight intensity={0.6} />
          <directionalLight position={[2, 2, 3]} intensity={1.2} />
          <Float speed={3} rotationIntensity={1.2}>
            <mesh>
              <icosahedronGeometry args={[1.5, 1]} />
              <MeshDistortMaterial
                color="#6366f1"
                attach="material"
                distort={0.4}
                speed={2}
                roughness={0.4}
              />
            </mesh>
          </Float>
          <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={2} />
        </Canvas>
      </div>

      {/* Hero Content */}
      <motion.div
        className="relative z-10 text-center px-6 md:px-12 max-w-3xl"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-6 bg-gradient-to-r from-indigo-300 via-white to-purple-300 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 1 }}
        >
          Build a Job-Ready Profile That Gets You Noticed 🚀
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 1 }}
        >
          Transform your{" "}
          <span className="text-indigo-300 font-semibold">LinkedIn</span> and{" "}
          <span className="text-purple-300 font-semibold">Naukri</span> profiles
          into professional-grade assets that attract recruiters and open doors.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <a
            href="#contact"
            className="inline-flex items-center bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-indigo-500/40 hover:scale-105 transition-all duration-300 font-semibold text-lg"
          >
            Improve My Profile
            <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </motion.div>
      </motion.div>

      {/* Decorative Wave at Bottom */}
      <svg
        className="absolute bottom-0 left-0 w-full"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 320"
      >
        <path
          fill="#ffffff"
          fillOpacity="1"
          d="M0,288L48,272C96,256,192,224,288,186.7C384,149,480,107,576,122.7C672,139,768,213,864,224C960,235,1056,181,1152,138.7C1248,96,1344,64,1392,48L1440,32L1440,320L0,320Z"
        ></path>
      </svg>
    </section>
  );
}
